import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { api } from '../api/client.js';
import { StorePublicHeader } from '../components/store/StorePublicHeader.jsx';
import { StorePwaBootstrap } from '../components/store/StorePwaBootstrap.jsx';
import { isReservedStoreSlug } from '../config/reservedSlugs.js';
import { useTheme } from '../theme/ThemeContext.jsx';
import { parseStoreAccent, storeAccentCssVariables } from '../theme/storeAccent.js';
import { formatBrl } from '../utils/currency.js';
import {
  ORDER_STATUS_BUTTON_ORDER,
  ORDER_STATUS_VALUES,
  orderIsPaidViaOnlineGateway,
  orderStatusLabelPtBr,
  orderOnlinePaymentEffectiveState,
  paymentStateCustomerChipDescriptor,
} from '../utils/orderStatus.js';
import { OrderErpObsInfoButton } from '../components/orders/OrderErpObsInfoButton.jsx';
import { CustomerRadarHistory } from '../components/store/customer-radar/CustomerRadarHistory.jsx';

/** Filtros da lista de pedidos (área do cliente): «Todos» + estados da API. */
const ORDER_CUSTOMER_FILTERS = [
  { id: 'all', label: 'Todos' },
  ...ORDER_STATUS_VALUES.map((id) => ({ id, label: orderStatusLabelPtBr(id) })),
];

const TABS = [
  { id: 'resumo', label: 'Resumo' },
  { id: 'dados', label: 'Dados pessoais' },
  { id: 'enderecos', label: 'Endereços' },
  { id: 'pedidos', label: 'Pedidos' },
];

function customerTokenKey(slug) {
  return `ominia.customer.${slug}`;
}

/** Mesmo prefixo que `StorePage` — ao sair da conta, não deixar sacola de visitante antiga no disco. */
const STORE_CART_LS_PREFIX = 'ominia.storeCart.v1.';

function statusStepClass(active, done) {
  if (active) return 'border-[rgb(var(--st-rgb))] bg-[rgb(var(--st-rgb)/0.12)] text-zinc-900 dark:text-zinc-50';
  if (done) return 'border-emerald-500/50 bg-emerald-500/10 text-emerald-900 dark:text-emerald-100';
  return 'border-zinc-200 bg-zinc-50/80 text-zinc-500 dark:border-zinc-700 dark:bg-zinc-800/50 dark:text-zinc-400';
}

const PIPE_RANK = { pending: 0, paid: 1, sent_to_erp: 2 };

function cepDigits(value) {
  return String(value || '').replace(/\D/g, '').slice(0, 8);
}

function formatCepInput(value) {
  const d = cepDigits(value);
  if (d.length <= 5) return d;
  return `${d.slice(0, 5)}-${d.slice(5)}`;
}

function formatCoordForInput(value) {
  if (value == null || value === '') return '';
  const n = Number(value);
  return Number.isFinite(n) ? String(n) : '';
}

function hasGeoCoords(lat, lng) {
  const la = String(lat ?? '').trim();
  const ln = String(lng ?? '').trim();
  if (la === '' || ln === '') return false;
  const latN = Number(la.replace(',', '.'));
  const lngN = Number(ln.replace(',', '.'));
  if (!Number.isFinite(latN) || !Number.isFinite(lngN)) return false;
  if (latN === 0 && lngN === 0) return false;
  return latN >= -90 && latN <= 90 && lngN >= -180 && lngN <= 180;
}

function geoStatusLabel(lat, lng) {
  return hasGeoCoords(lat, lng) ? 'Localizado no mapa' : 'Sem coordenadas — corrija o CEP ou preencha lat/lng';
}

/** Campos opcionais de latitude/longitude (correção manual). */
function GeoCoordFields({ latitude, longitude, onLatitude, onLongitude }) {
  return (
    <div className="rounded-xl border border-dashed border-zinc-200 bg-zinc-50/80 p-3 dark:border-zinc-700 dark:bg-zinc-900/50">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
        Coordenadas (opcional)
      </p>
      <p className="mt-1 text-[11px] leading-relaxed text-zinc-500 dark:text-zinc-400">
        Se o CEP não localizar automaticamente, preencha latitude e longitude (ex.: do Google Maps). Deixe vazio para
        tentar de novo pelo CEP ao guardar.
      </p>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="block text-xs font-semibold text-zinc-600 dark:text-zinc-400">
          Latitude
          <input
            inputMode="decimal"
            placeholder="Ex.: -16.686891"
            className="mt-1 w-full rounded-xl border border-zinc-200 bg-white px-3 py-2 font-mono text-sm dark:border-zinc-700 dark:bg-zinc-900"
            value={latitude}
            onChange={(e) => onLatitude(e.target.value)}
          />
        </label>
        <label className="block text-xs font-semibold text-zinc-600 dark:text-zinc-400">
          Longitude
          <input
            inputMode="decimal"
            placeholder="Ex.: -49.264794"
            className="mt-1 w-full rounded-xl border border-zinc-200 bg-white px-3 py-2 font-mono text-sm dark:border-zinc-700 dark:bg-zinc-900"
            value={longitude}
            onChange={(e) => onLongitude(e.target.value)}
          />
        </label>
      </div>
    </div>
  );
}

/** Junta ViaCEP num texto de endereço (Brasil). */
function enderecoFromViaCep(j) {
  if (!j || typeof j !== 'object') return '';
  const lines = [];
  const log = String(j.logradouro || '').trim();
  const comp = String(j.complemento || '').trim();
  if (log) lines.push(comp ? `${log} (${comp})` : log);
  const bairro = String(j.bairro || '').trim();
  if (bairro) lines.push(bairro);
  const loc = [String(j.localidade || '').trim(), String(j.uf || '').trim()].filter(Boolean).join(' - ');
  if (loc) lines.push(loc);
  const cep = String(j.cep || '').trim();
  if (cep) lines.push(`CEP ${cep}`);
  return lines.join('\n');
}

/** Área do cliente na loja pública: `/:slug/conta` — resumo (BI), dados, endereços, histórico de pedidos (só leitura). */
export function StoreCustomerAccountPage() {
  const { slug: slugParam } = useParams();
  const navigate = useNavigate();
  const slug = slugParam ? String(slugParam).toLowerCase() : '';
  const { scheme, toggle } = useTheme();
  const [tab, setTab] = useState('resumo');
  const [tenant, setTenant] = useState(null);
  const [loadErr, setLoadErr] = useState('');
  const [token, setToken] = useState('');
  const [session, setSession] = useState(null);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [stats, setStats] = useState(null);
  const [radarEnabled, setRadarEnabled] = useState(false);
  const [savedAddrCount, setSavedAddrCount] = useState(0);
  const [orders, setOrders] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [addrSchemaNote, setAddrSchemaNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const [profile, setProfile] = useState({
    nome: '',
    email: '',
    telefone: '',
    whatsapp_country_code: '+351',
    endereco: '',
    cep: '',
    latitude: '',
    longitude: '',
  });
  const [addrForm, setAddrForm] = useState({
    label: '',
    endereco: '',
    cep: '',
    latitude: '',
    longitude: '',
    is_default: false,
  });
  const [cepBusy, setCepBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  /** `'all'` | estado API (`pending`, `paid`, …) */
  const [orderStatusFilter, setOrderStatusFilter] = useState('all');

  const resumoStats = useMemo(
    () => stats ?? { orders_total: 0, total_spent_brl: 0, last_order_at: null, by_status: {} },
    [stats],
  );

  const visibleTabs = useMemo(
    () => (radarEnabled ? [...TABS, { id: 'atividade', label: 'Minha atividade' }] : TABS),
    [radarEnabled],
  );

  const accentStyle = useMemo(
    () => storeAccentCssVariables(parseStoreAccent(tenant?.theme_json)),
    [tenant?.theme_json],
  );

  const lojaPath = slug ? `/${encodeURIComponent(slug)}` : '/';
  const nomeLoja = tenant?.nome ? String(tenant.nome) : slug || 'Loja';

  const { filteredOrders, orderStatusCounts } = useMemo(() => {
    const counts = { pending: 0, paid: 0, sent_to_erp: 0, cancelled: 0 };
    for (const o of orders) {
      const s = String(o.status || 'pending');
      if (s in counts) counts[s] += 1;
    }
    const filtered =
      orderStatusFilter === 'all'
        ? orders
        : orders.filter((o) => String(o.status || 'pending') === orderStatusFilter);
    return { filteredOrders: filtered, orderStatusCounts: counts };
  }, [orders, orderStatusFilter]);

  const authHeaders = useMemo(() => {
    if (!token) return {};
    return { 'X-Customer-Token': token };
  }, [token]);

  const reloadSession = useCallback(async () => {
    if (!slug || !token) {
      setSession(null);
      setStats(null);
      setSessionChecked(true);
      return;
    }
    setSessionChecked(false);
    try {
      const res = await api(`/store/${encodeURIComponent(slug)}/customer/session`, {
        skipAuth: true,
        headers: authHeaders,
      });
      if (res?.logged_in && res?.customer) {
        setSession(res.customer);
        setStats(res.stats || null);
        setRadarEnabled(Boolean(res.radar_enabled));
        setSavedAddrCount(Number(res.saved_addresses_count ?? 0));
        const c = res.customer;
        setProfile({
          nome: String(c.nome || ''),
          email: String(c.email || ''),
          telefone: String(c.telefone || ''),
          whatsapp_country_code: String(c.whatsapp_country_code || '+351'),
          endereco: String(c.endereco || ''),
          cep: c.cep ? formatCepInput(String(c.cep)) : '',
          latitude: formatCoordForInput(c.latitude),
          longitude: formatCoordForInput(c.longitude),
        });
      } else {
        setSession(null);
        setStats(null);
      }
    } catch {
      setSession(null);
      setStats(null);
    } finally {
      setSessionChecked(true);
    }
  }, [slug, token, authHeaders]);

  const reloadOrders = useCallback(async () => {
    if (!slug || !token) return;
    const d = await api(`/store/${encodeURIComponent(slug)}/customer/orders?limit=50`, {
      skipAuth: true,
      headers: authHeaders,
    });
    setOrders(Array.isArray(d.orders) ? d.orders : []);
  }, [slug, token, authHeaders]);

  const reloadAddresses = useCallback(async () => {
    if (!slug || !token) return;
    try {
      const d = await api(`/store/${encodeURIComponent(slug)}/customer/addresses`, {
        skipAuth: true,
        headers: authHeaders,
      });
      setAddresses(Array.isArray(d.addresses) ? d.addresses : []);
      setAddrSchemaNote(typeof d.schema_note === 'string' ? d.schema_note : '');
    } catch {
      setAddresses([]);
      setAddrSchemaNote('');
    }
  }, [slug, token, authHeaders]);

  useEffect(() => {
    if (!slug || typeof window === 'undefined') return;
    setToken(String(window.localStorage.getItem(customerTokenKey(slug)) || ''));
  }, [slug]);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    api(`/store/${encodeURIComponent(slug)}/catalog`, { skipAuth: true })
      .then((payload) => {
        if (cancelled) return;
        const t = payload?.tenant;
        setTenant(t && typeof t === 'object' ? t : null);
        setLoadErr('');
      })
      .catch((e) => {
        if (cancelled) return;
        setLoadErr(e.message || 'Não foi possível carregar a loja.');
        setTenant(null);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    if (!token || !slug) return;
    reloadSession().catch(() => setSession(null));
  }, [token, slug, reloadSession]);

  useEffect(() => {
    if (!token || !slug || tab !== 'pedidos') return;
    reloadOrders().catch(() => setOrders([]));
  }, [token, slug, tab, reloadOrders]);

  useEffect(() => {
    if (!token || !slug || tab !== 'enderecos') return;
    reloadAddresses().catch(() => {});
  }, [token, slug, tab, reloadAddresses]);

  function logoutCustomer() {
    if (!slug || typeof window === 'undefined') return;
    try {
      window.localStorage.removeItem(customerTokenKey(slug));
      const sk = String(slug).toLowerCase();
      window.localStorage.removeItem(`${STORE_CART_LS_PREFIX}${sk}`);
    } catch {
      /* ignore */
    }
    setToken('');
    setSession(null);
    navigate(lojaPath, { replace: true });
  }

  async function buscarCep(target, e) {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    setErr('');
    const cepValue = target === 'profile' ? profile.cep : addrForm.cep;
    const d8 = cepDigits(cepValue);
    if (d8.length !== 8) {
      setErr('Informe o CEP com 8 dígitos.');
      return;
    }
    setCepBusy(true);
    try {
      const r = await fetch(`https://viacep.com.br/ws/${d8}/json/`);
      const j = await r.json();
      if (!r.ok || j == null || typeof j !== 'object' || j.erro === true || j.erro === 'true') {
        setErr('CEP não encontrado.');
        return;
      }
      const texto = enderecoFromViaCep(j);
      if (!texto.trim()) {
        setErr('CEP encontrado mas sem dados de endereço.');
        return;
      }
      const formattedCep = formatCepInput(d8);
      if (target === 'profile') {
        setProfile((p) => ({ ...p, cep: formattedCep, endereco: texto, latitude: '', longitude: '' }));
      } else {
        setAddrForm((a) => ({ ...a, cep: formattedCep, endereco: texto, latitude: '', longitude: '' }));
      }
      setMsg('Endereço preenchido a partir do CEP. Complete número e complemento se precisar.');
    } catch {
      setErr('Não foi possível consultar o CEP. Tente de novo.');
    } finally {
      setCepBusy(false);
    }
  }

  async function saveEditedAddress(e) {
    e.preventDefault();
    if (!editing) return;
    setErr('');
    setMsg('');
    if (!String(editing.endereco || '').trim()) {
      setErr('Indique o texto do endereço.');
      return;
    }
    setBusy(true);
    try {
      await api(`/store/${encodeURIComponent(slug)}/customer/addresses/${editing.id}`, {
        skipAuth: true,
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        method: 'PATCH',
        body: JSON.stringify({
          label: String(editing.label || '').trim(),
          endereco: String(editing.endereco || '').trim(),
          cep: cepDigits(editing.cep || ''),
          latitude: String(editing.latitude ?? '').trim(),
          longitude: String(editing.longitude ?? '').trim(),
          is_default: !!editing.is_default,
        }),
      });
      setMsg('Endereço actualizado.');
      setEditing(null);
      await reloadAddresses();
      await reloadSession();
    } catch (errSave) {
      setErr(errSave.message || 'Erro ao gravar.');
    } finally {
      setBusy(false);
    }
  }

  async function saveProfile(e) {
    e.preventDefault();
    setErr('');
    setMsg('');
    setBusy(true);
    try {
      await api(`/store/${encodeURIComponent(slug)}/customer/profile`, {
        skipAuth: true,
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        method: 'PATCH',
        body: JSON.stringify({
          nome: profile.nome.trim(),
          email: profile.email.trim(),
          telefone: profile.telefone.trim(),
          whatsapp_country_code: profile.whatsapp_country_code.trim(),
          endereco: profile.endereco.trim(),
          cep: cepDigits(profile.cep),
          latitude: profile.latitude.trim(),
          longitude: profile.longitude.trim(),
        }),
      });
      setMsg('Dados actualizados.');
      await reloadSession();
    } catch (e) {
      setErr(e.message || 'Erro ao gravar.');
    } finally {
      setBusy(false);
    }
  }

  async function addAddress(e) {
    e.preventDefault();
    setErr('');
    setMsg('');
    if (!addrForm.endereco.trim()) {
      setErr('Indique o texto do endereço.');
      return;
    }
    setBusy(true);
    try {
      await api(`/store/${encodeURIComponent(slug)}/customer/addresses`, {
        skipAuth: true,
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        method: 'POST',
        body: JSON.stringify({
          label: addrForm.label.trim(),
          endereco: addrForm.endereco.trim(),
          cep: cepDigits(addrForm.cep),
          latitude: addrForm.latitude.trim(),
          longitude: addrForm.longitude.trim(),
          is_default: addrForm.is_default,
        }),
      });
      setMsg('Endereço adicionado.');
      setAddrForm({ label: '', endereco: '', cep: '', latitude: '', longitude: '', is_default: false });
      await reloadAddresses();
      await reloadSession();
    } catch (e) {
      setErr(e.message || 'Erro ao adicionar endereço.');
    } finally {
      setBusy(false);
    }
  }

  async function removeAddress(id) {
    if (!window.confirm('Remover este endereço?')) return;
    setErr('');
    setBusy(true);
    try {
      await api(`/store/${encodeURIComponent(slug)}/customer/addresses/${id}`, {
        skipAuth: true,
        headers: authHeaders,
        method: 'DELETE',
      });
      setMsg('Endereço removido.');
      if (editing?.id === id) setEditing(null);
      await reloadAddresses();
      await reloadSession();
    } catch (e) {
      setErr(e.message || 'Erro ao remover.');
    } finally {
      setBusy(false);
    }
  }

  if (!slug || isReservedStoreSlug(slug)) {
    return <Navigate to="/" replace />;
  }

  if (loadErr) {
    return (
      <div className="min-h-dvh bg-zinc-50 text-zinc-900 dark:bg-zinc-950 dark:text-zinc-100" style={accentStyle}>
        <StorePublicHeader
          slug={slug}
          tenant={tenant}
          scheme={scheme}
          onToggleTheme={toggle}
          customerSession={null}
          onLogout={() => {}}
        />
        <div className="mx-auto max-w-7xl px-4 py-10 text-center text-sm text-zinc-600 dark:text-zinc-300">
          <p className="font-medium">{loadErr}</p>
          <Link className="mt-4 inline-block text-[rgb(var(--st-rgb))] underline" to={lojaPath}>
            Voltar à loja
          </Link>
        </div>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="min-h-dvh bg-zinc-50 text-zinc-900 dark:bg-zinc-950 dark:text-zinc-100" style={accentStyle}>
        <StorePublicHeader
          slug={slug}
          tenant={tenant}
          scheme={scheme}
          onToggleTheme={toggle}
          customerSession={null}
          onLogout={() => {}}
        />
        <div className="mx-auto max-w-7xl px-4 py-10">
          <div className="mx-auto max-w-md rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
            <p className="text-center text-sm text-zinc-600 dark:text-zinc-300">
              Inicie sessão na loja para ver a sua área de cliente.
            </p>
            <Link
              to={lojaPath}
              className="mt-5 flex w-full justify-center rounded-xl bg-zinc-900 py-3 text-sm font-semibold text-white dark:bg-zinc-100 dark:text-zinc-900"
            >
              Ir para {nomeLoja}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (token && !sessionChecked) {
    return (
      <div className="min-h-dvh bg-zinc-50 text-zinc-900 dark:bg-zinc-950 dark:text-zinc-100" style={accentStyle}>
        <StorePublicHeader
          slug={slug}
          tenant={tenant}
          scheme={scheme}
          onToggleTheme={toggle}
          customerSession={null}
          onLogout={() => {}}
        />
        <div className="flex justify-center px-4 py-16">
          <p className="text-sm text-zinc-500">A carregar…</p>
        </div>
      </div>
    );
  }

  if (token && sessionChecked && !session) {
    return (
      <div className="min-h-dvh bg-zinc-50 text-zinc-900 dark:bg-zinc-950 dark:text-zinc-100" style={accentStyle}>
        <StorePublicHeader
          slug={slug}
          tenant={tenant}
          scheme={scheme}
          onToggleTheme={toggle}
          customerSession={null}
          onLogout={() => {}}
        />
        <div className="mx-auto max-w-7xl px-4 py-10 text-center">
          <p className="text-sm text-zinc-600 dark:text-zinc-300">Sessão inválida ou expirada.</p>
          <Link to={lojaPath} className="mt-4 inline-block font-semibold text-[rgb(var(--st-rgb))] underline">
            Voltar à loja
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-zinc-50 text-zinc-900 dark:bg-zinc-950 dark:text-zinc-100" style={accentStyle}>
      <StorePwaBootstrap slug={slug} tenant={tenant} />
      <StorePublicHeader
        slug={slug}
        tenant={tenant}
        scheme={scheme}
        onToggleTheme={toggle}
        customerSession={{ nome: session?.nome }}
        onLogout={logoutCustomer}
        contaPageActive
      />

      <main className="mx-auto max-w-7xl px-3 py-6 sm:px-5 sm:py-8 lg:px-6">
        <h1 className="font-display text-xl font-bold tracking-tight sm:text-2xl">A minha conta</h1>
        <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
          Olá, <span className="font-medium text-zinc-800 dark:text-zinc-200">{session?.nome || 'cliente'}</span> — resumo,
          dados, endereços e pedidos nesta loja.
        </p>

        <nav className="mt-6 flex flex-wrap gap-1.5 border-b border-zinc-200 pb-2 dark:border-zinc-800">
          {visibleTabs.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => {
                setTab(t.id);
                setMsg('');
                setErr('');
                setEditing(null);
                if (t.id !== 'pedidos') setOrderStatusFilter('all');
              }}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold uppercase tracking-wide transition sm:text-[13px] ${
                tab === t.id
                  ? 'bg-[rgb(var(--st-rgb))] text-white shadow-sm'
                  : 'text-zinc-600 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:bg-zinc-800/80'
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>

        {err ? (
          <p className="mt-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200">
            {err}
          </p>
        ) : null}
        {msg ? (
          <p className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-100">
            {msg}
          </p>
        ) : null}

        {tab === 'resumo' && session ? (
          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            <div className="rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm dark:border-zinc-800 dark:bg-zinc-900/80">
              <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-400">Pedidos (total)</p>
              <p className="mt-1 text-2xl font-bold tabular-nums">{resumoStats.orders_total}</p>
            </div>
            <div className="rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm dark:border-zinc-800 dark:bg-zinc-900/80">
              <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-400">Total pago (pago / enviado ERP)</p>
              <p className="mt-1 text-2xl font-bold tabular-nums">{formatBrl(resumoStats.total_spent_brl)}</p>
            </div>
            <div className="rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm dark:border-zinc-800 dark:bg-zinc-900/80">
              <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-400">Endereços guardados</p>
              <p className="mt-1 text-2xl font-bold tabular-nums">{savedAddrCount}</p>
            </div>
            <div className="rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm dark:border-zinc-800 dark:bg-zinc-900/80">
              <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-400">Último pedido</p>
              <p className="mt-1 text-sm font-medium leading-snug text-zinc-700 dark:text-zinc-200">
                {resumoStats.last_order_at ? String(resumoStats.last_order_at).slice(0, 16).replace('T', ' ') : '—'}
              </p>
            </div>
            <div className="sm:col-span-2 rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm dark:border-zinc-800 dark:bg-zinc-900/80">
              <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-400">Pedidos por estado</p>
              <ul className="mt-2 flex flex-wrap gap-2">
                {Object.keys(resumoStats.by_status || {}).length === 0 ? (
                  <li className="text-sm text-zinc-500">Sem pedidos ainda.</li>
                ) : (
                  Object.entries(resumoStats.by_status).map(([k, v]) => (
                    <li
                      key={k}
                      className="rounded-lg border border-zinc-200 bg-zinc-50 px-2.5 py-1 text-xs font-medium dark:border-zinc-700 dark:bg-zinc-800/60"
                    >
                      {orderStatusLabelPtBr(k)} <span className="tabular-nums text-zinc-500">({v})</span>
                    </li>
                  ))
                )}
              </ul>
            </div>
          </div>
        ) : null}

        {tab === 'dados' ? (
          <form className="mt-6 max-w-lg space-y-3" onSubmit={saveProfile}>
            <label className="block text-xs font-semibold text-zinc-600 dark:text-zinc-400">
              Nome
              <input
                className="mt-1 w-full rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
                value={profile.nome}
                onChange={(e) => setProfile((p) => ({ ...p, nome: e.target.value }))}
                required
              />
            </label>
            <label className="block text-xs font-semibold text-zinc-600 dark:text-zinc-400">
              E-mail
              <input
                type="email"
                className="mt-1 w-full rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
                value={profile.email}
                onChange={(e) => setProfile((p) => ({ ...p, email: e.target.value }))}
              />
            </label>
            <label className="block text-xs font-semibold text-zinc-600 dark:text-zinc-400">
              Prefixo WhatsApp
              <input
                className="mt-1 w-full rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
                value={profile.whatsapp_country_code}
                onChange={(e) => setProfile((p) => ({ ...p, whatsapp_country_code: e.target.value }))}
              />
            </label>
            <label className="block text-xs font-semibold text-zinc-600 dark:text-zinc-400">
              Telefone / WhatsApp
              <input
                className="mt-1 w-full rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
                value={profile.telefone}
                onChange={(e) => setProfile((p) => ({ ...p, telefone: e.target.value }))}
              />
            </label>

            <div className="border-t border-zinc-200 pt-4 dark:border-zinc-800">
              <h2 className="text-sm font-bold text-zinc-800 dark:text-zinc-100">Morada principal (casa)</h2>
              <p className="mt-1 text-[11px] text-zinc-500 dark:text-zinc-400">
                Usada no cadastro e no mapa da loja. Corrija o CEP ou informe coordenadas manualmente se a localização
                automática falhar.
              </p>
              <p
                className={`mt-2 inline-flex rounded-lg px-2 py-1 text-[10px] font-semibold ${
                  hasGeoCoords(profile.latitude, profile.longitude)
                    ? 'bg-emerald-500/15 text-emerald-800 dark:text-emerald-200'
                    : 'bg-amber-500/15 text-amber-900 dark:text-amber-100'
                }`}
              >
                {geoStatusLabel(profile.latitude, profile.longitude)}
              </p>
              <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-end">
                <label className="block min-w-0 flex-1 text-xs font-semibold text-zinc-600 dark:text-zinc-400">
                  CEP (Brasil)
                  <input
                    inputMode="numeric"
                    autoComplete="postal-code"
                    placeholder="00000-000"
                    maxLength={9}
                    className="mt-1 w-full rounded-xl border border-zinc-200 bg-white px-3 py-2 font-mono text-sm tracking-wide dark:border-zinc-700 dark:bg-zinc-900"
                    value={profile.cep}
                    onChange={(e) => setProfile((p) => ({ ...p, cep: formatCepInput(e.target.value) }))}
                  />
                </label>
                <button
                  type="button"
                  disabled={busy || cepBusy || cepDigits(profile.cep).length !== 8}
                  onClick={(e) => buscarCep('profile', e)}
                  className="shrink-0 rounded-xl border border-zinc-300 bg-zinc-100 px-4 py-2.5 text-sm font-semibold text-zinc-800 transition hover:bg-zinc-200 disabled:opacity-50 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100 dark:hover:bg-zinc-700"
                >
                  {cepBusy ? 'A buscar…' : 'Buscar CEP'}
                </button>
              </div>
              <label className="mt-3 block text-xs font-semibold text-zinc-600 dark:text-zinc-400">
                Endereço completo (rua, nº, bairro, cidade…)
                <textarea
                  rows={4}
                  className="mt-1 w-full rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
                  placeholder="Rua e número, complemento, referências…"
                  value={profile.endereco}
                  onChange={(e) => setProfile((p) => ({ ...p, endereco: e.target.value }))}
                />
              </label>
              <div className="mt-3">
                <GeoCoordFields
                  latitude={profile.latitude}
                  longitude={profile.longitude}
                  onLatitude={(v) => setProfile((p) => ({ ...p, latitude: v }))}
                  onLongitude={(v) => setProfile((p) => ({ ...p, longitude: v }))}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={busy}
              className="rounded-xl bg-zinc-900 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-60 dark:bg-zinc-100 dark:text-zinc-900"
            >
              Guardar dados
            </button>
          </form>
        ) : null}

        {tab === 'enderecos' ? (
          <div className="mt-6 space-y-8">
            {addrSchemaNote ? (
              <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-100">
                {addrSchemaNote}
              </p>
            ) : null}
            <div className="grid gap-8 xl:grid-cols-[minmax(0,26rem)_minmax(0,1fr)] xl:items-start">
              <form
                className="space-y-3 rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm dark:border-zinc-800 dark:bg-zinc-900/80 xl:max-w-none"
                onSubmit={addAddress}
              >
                <h2 className="text-sm font-bold text-zinc-800 dark:text-zinc-100">Novo endereço de entrega</h2>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                  <label className="block min-w-0 flex-1 text-xs font-semibold text-zinc-600 dark:text-zinc-400">
                    CEP (Brasil)
                    <input
                      inputMode="numeric"
                      autoComplete="postal-code"
                      placeholder="00000-000"
                      maxLength={9}
                      className="mt-1 w-full rounded-xl border border-zinc-200 bg-white px-3 py-2 font-mono text-sm tracking-wide dark:border-zinc-700 dark:bg-zinc-900"
                      value={addrForm.cep}
                      onChange={(e) => setAddrForm((a) => ({ ...a, cep: formatCepInput(e.target.value) }))}
                    />
                  </label>
                  <button
                    type="button"
                    disabled={busy || cepBusy || cepDigits(addrForm.cep).length !== 8}
                    onClick={(e) => buscarCep('addr', e)}
                    className="shrink-0 rounded-xl border border-zinc-300 bg-zinc-100 px-4 py-2.5 text-sm font-semibold text-zinc-800 transition hover:bg-zinc-200 disabled:opacity-50 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100 dark:hover:bg-zinc-700"
                  >
                    {cepBusy ? 'A buscar…' : 'Buscar CEP'}
                  </button>
                </div>
                <p className="text-[11px] leading-relaxed text-zinc-500 dark:text-zinc-400">
                  Pesquisa automática (ViaCEP). Depois complete número, apartamento ou ponto de referência no campo abaixo.
                </p>
                <label className="block text-xs font-semibold text-zinc-600 dark:text-zinc-400">
                  Rótulo (opcional)
                  <input
                    className="mt-1 w-full rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
                    placeholder="Ex.: Casa, Trabalho"
                    value={addrForm.label}
                    onChange={(e) => setAddrForm((a) => ({ ...a, label: e.target.value }))}
                  />
                </label>
                <label className="block text-xs font-semibold text-zinc-600 dark:text-zinc-400">
                  Endereço completo (rua, nº, bairro, cidade…)
                  <textarea
                    required
                    rows={5}
                    className="mt-1 w-full rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
                    placeholder="Rua e número, complemento, referências…"
                    value={addrForm.endereco}
                    onChange={(e) => setAddrForm((a) => ({ ...a, endereco: e.target.value }))}
                  />
                </label>
                <GeoCoordFields
                  latitude={addrForm.latitude}
                  longitude={addrForm.longitude}
                  onLatitude={(v) => setAddrForm((a) => ({ ...a, latitude: v }))}
                  onLongitude={(v) => setAddrForm((a) => ({ ...a, longitude: v }))}
                />
                <label className="flex items-center gap-2 text-xs font-medium text-zinc-700 dark:text-zinc-300">
                  <input
                    type="checkbox"
                    checked={addrForm.is_default}
                    onChange={(e) => setAddrForm((a) => ({ ...a, is_default: e.target.checked }))}
                  />
                  Definir como endereço padrão no checkout
                </label>
                <button
                  type="submit"
                  disabled={busy}
                  className="w-full rounded-xl bg-[rgb(var(--st-rgb))] px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-60 sm:w-auto"
                >
                  Adicionar endereço
                </button>
              </form>

              <div className="min-w-0">
                <h2 className="text-sm font-bold text-zinc-800 dark:text-zinc-100">Meus endereços</h2>
                <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
                  Lista guardada para escolher no checkout. Pode editar ou excluir a qualquer momento.
                </p>
                <ul className="mt-4 space-y-3">
                  {addresses.map((a) => (
                    <li
                      key={a.id}
                      className="rounded-2xl border border-zinc-200 bg-white p-4 text-sm shadow-sm dark:border-zinc-800 dark:bg-zinc-900/80"
                    >
                      {editing && editing.id === a.id ? (
                        <form className="space-y-3" onSubmit={saveEditedAddress}>
                          <p className="text-xs font-bold uppercase tracking-wide text-zinc-400">A editar</p>
                          <label className="block text-xs font-semibold text-zinc-600 dark:text-zinc-400">
                            Rótulo (opcional)
                            <input
                              className="mt-1 w-full rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
                              value={editing.label}
                              onChange={(e) => setEditing((ed) => (ed ? { ...ed, label: e.target.value } : ed))}
                            />
                          </label>
                          <label className="block text-xs font-semibold text-zinc-600 dark:text-zinc-400">
                            CEP (Brasil)
                            <input
                              inputMode="numeric"
                              placeholder="00000-000"
                              maxLength={9}
                              className="mt-1 w-full rounded-xl border border-zinc-200 bg-white px-3 py-2 font-mono text-sm dark:border-zinc-700 dark:bg-zinc-900"
                              value={editing.cep || ''}
                              onChange={(e) =>
                                setEditing((ed) => (ed ? { ...ed, cep: formatCepInput(e.target.value) } : ed))
                              }
                            />
                          </label>
                          <label className="block text-xs font-semibold text-zinc-600 dark:text-zinc-400">
                            Endereço
                            <textarea
                              required
                              rows={4}
                              className="mt-1 w-full rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
                              value={editing.endereco}
                              onChange={(e) => setEditing((ed) => (ed ? { ...ed, endereco: e.target.value } : ed))}
                            />
                          </label>
                          <GeoCoordFields
                            latitude={editing.latitude || ''}
                            longitude={editing.longitude || ''}
                            onLatitude={(v) => setEditing((ed) => (ed ? { ...ed, latitude: v } : ed))}
                            onLongitude={(v) => setEditing((ed) => (ed ? { ...ed, longitude: v } : ed))}
                          />
                          <label className="flex items-center gap-2 text-xs font-medium text-zinc-700 dark:text-zinc-300">
                            <input
                              type="checkbox"
                              checked={editing.is_default}
                              onChange={(e) => setEditing((ed) => (ed ? { ...ed, is_default: e.target.checked } : ed))}
                            />
                            Endereço padrão no checkout
                          </label>
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="submit"
                              disabled={busy}
                              className="rounded-xl bg-[rgb(var(--st-rgb))] px-4 py-2 text-xs font-semibold text-white disabled:opacity-60"
                            >
                              Guardar
                            </button>
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => setEditing(null)}
                              className="rounded-xl border border-zinc-300 px-4 py-2 text-xs font-semibold text-zinc-700 dark:border-zinc-600 dark:text-zinc-200"
                            >
                              Cancelar
                            </button>
                          </div>
                        </form>
                      ) : (
                        <>
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                            <div className="min-w-0 flex-1">
                              <p className="font-semibold text-zinc-800 dark:text-zinc-100">
                                {a.label ? String(a.label) : 'Endereço'}{' '}
                                {Number(a.is_default) === 1 ? (
                                  <span className="ml-1 rounded-md bg-[rgb(var(--st-rgb)/0.15)] px-1.5 py-0.5 text-[10px] font-bold uppercase text-[rgb(var(--st-rgb))]">
                                    Padrão
                                  </span>
                                ) : null}
                              </p>
                              {a.cep ? (
                                <p className="mt-1 font-mono text-[11px] text-zinc-500">CEP {formatCepInput(String(a.cep))}</p>
                              ) : null}
                              <p className="mt-2 whitespace-pre-wrap text-zinc-600 dark:text-zinc-300">{a.endereco}</p>
                              <p
                                className={`mt-2 inline-flex rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${
                                  hasGeoCoords(a.latitude, a.longitude)
                                    ? 'bg-emerald-500/15 text-emerald-800 dark:text-emerald-200'
                                    : 'bg-amber-500/15 text-amber-900 dark:text-amber-100'
                                }`}
                              >
                                {geoStatusLabel(a.latitude, a.longitude)}
                              </p>
                            </div>
                            <div className="flex shrink-0 flex-wrap gap-2 sm:flex-col sm:items-stretch">
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() =>
                                  setEditing({
                                    id: a.id,
                                    label: String(a.label ?? ''),
                                    endereco: String(a.endereco ?? ''),
                                    cep: a.cep ? formatCepInput(String(a.cep)) : '',
                                    latitude: formatCoordForInput(a.latitude),
                                    longitude: formatCoordForInput(a.longitude),
                                    is_default: Number(a.is_default) === 1,
                                  })
                                }
                                className="rounded-lg border border-zinc-200 px-3 py-1.5 text-xs font-semibold text-zinc-800 dark:border-zinc-600 dark:text-zinc-100"
                              >
                                Editar
                              </button>
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => removeAddress(a.id)}
                                className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-700 dark:border-red-900/50 dark:text-red-300"
                              >
                                Excluir
                              </button>
                            </div>
                          </div>
                        </>
                      )}
                    </li>
                  ))}
                  {!addresses.length && !addrSchemaNote ? (
                    <li className="rounded-2xl border border-dashed border-zinc-300 bg-zinc-50/80 px-4 py-8 text-center text-sm text-zinc-500 dark:border-zinc-700 dark:bg-zinc-900/40 dark:text-zinc-400">
                      Ainda não guardou endereços. Adicione um pelo formulário para usar no checkout.
                    </li>
                  ) : null}
                </ul>
              </div>
            </div>
          </div>
        ) : null}

        {tab === 'pedidos' ? (
          <div className="mt-6 space-y-4">
            <p className="text-xs text-zinc-500 dark:text-zinc-400">
              Acompanhe o estado dos seus pedidos. A loja é quem actualiza o estado — aqui é apenas consulta.
            </p>
            <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filtrar por estado do pedido">
              {ORDER_CUSTOMER_FILTERS.map((f) => {
                const count =
                  f.id === 'all' ? orders.length : (orderStatusCounts[f.id] ?? 0);
                const active = orderStatusFilter === f.id;
                return (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => setOrderStatusFilter(f.id)}
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold uppercase tracking-wide transition sm:text-[13px] ${
                      active
                        ? 'bg-[rgb(var(--st-rgb))] text-white shadow-sm'
                        : 'border border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-900/80 dark:text-zinc-400 dark:hover:bg-zinc-800/80'
                    }`}
                  >
                    {f.label}
                    <span
                      className={`tabular-nums text-[10px] font-bold sm:text-[11px] ${
                        active ? 'text-white/90' : 'text-zinc-400 dark:text-zinc-500'
                      }`}
                    >
                      ({count})
                    </span>
                  </button>
                );
              })}
            </div>
            {filteredOrders.map((o) => {
              const st = String(o.status || 'pending');
              // Mesma ordem dos botões usada no painel do administrador da loja
              // (`OrdersPage`), exceto «cancelled» — que na visão do cliente fica
              // num parágrafo destacado em vermelho, e não como mais um chip.
              const steps = ORDER_STATUS_BUTTON_ORDER.filter((s) => s !== 'cancelled');
              const cancelled = st === 'cancelled';
              const cr = PIPE_RANK[st] ?? -1;
              const paidOnline = orderIsPaidViaOnlineGateway(o);
              // database/0044_payment_state.sql — descritor "consumer-friendly".
              // Quando o backend (deploy antigo) não devolve `payment_state`, o
              // helper devolve null e a UI fica exactamente como era.
              const payCustomerChip = paymentStateCustomerChipDescriptor(o);
              const effPayState = orderOnlinePaymentEffectiveState(o);
              // Quando o pedido foi cancelado pelo cron por causa de MP,
              // adaptamos a frase para o cliente perceber a razão (e que pode
              // tentar de novo). Caso contrário ficamos com a frase neutra.
              const cancellationReasonText =
                cancelled && effPayState === 'expired'
                  ? 'Pedido cancelado: o pagamento não foi concluído no Mercado Pago a tempo. Os itens voltaram para a sua sacola — basta finalizar novamente.'
                  : cancelled && effPayState === 'failed'
                  ? 'Pedido cancelado: o Mercado Pago recusou o pagamento. Os itens voltaram para a sua sacola — tente novamente, talvez com outro meio.'
                  : cancelled
                  ? 'Pedido cancelado.'
                  : '';
              return (
                <article
                  key={o.id}
                  className="rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm dark:border-zinc-800 dark:bg-zinc-900/80"
                >
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <p className="font-mono text-xs text-zinc-500">#{o.id}</p>
                    <p className="text-xs text-zinc-500">{String(o.created_at || '').slice(0, 19).replace('T', ' ')}</p>
                  </div>
                  <p className="mt-1 text-lg font-bold text-zinc-900 dark:text-zinc-50">{formatBrl(o.total)}</p>
                  <p className="mt-0.5 text-sm font-semibold text-[rgb(var(--st-rgb))]">
                    {orderStatusLabelPtBr(st)}
                    {paidOnline ? ' · $ Online' : ''}
                  </p>
                  {payCustomerChip ? (
                    <p
                      title={payCustomerChip.tooltip}
                      className={`mt-1 inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${
                        payCustomerChip.tone === 'success'
                          ? 'border-emerald-500/45 bg-emerald-50 text-emerald-900 dark:border-emerald-300/35 dark:bg-emerald-950/25 dark:text-emerald-100'
                          : payCustomerChip.tone === 'warn'
                          ? 'animate-pulse border-amber-500/45 bg-amber-50 text-amber-900 dark:border-amber-300/35 dark:bg-amber-950/25 dark:text-amber-100'
                          : payCustomerChip.tone === 'danger'
                          ? 'border-rose-500/45 bg-rose-50 text-rose-900 dark:border-rose-300/35 dark:bg-rose-950/25 dark:text-rose-100'
                          : 'border-zinc-300/60 bg-zinc-50 text-zinc-700 dark:border-zinc-600/40 dark:bg-zinc-800/60 dark:text-zinc-200'
                      }`}
                    >
                      {payCustomerChip.icon} {payCustomerChip.label}
                    </p>
                  ) : null}
                  {!cancelled ? (
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      {steps.map((s) => {
                        const sr = PIPE_RANK[s] ?? 0;
                        const done = cr >= 0 && cr > sr;
                        const active = st === s;
                        return (
                          <div
                            key={s}
                            className={`rounded-lg border px-2 py-1 text-[10px] font-bold uppercase tracking-wide ${statusStepClass(active, done)}`}
                          >
                            {orderStatusLabelPtBr(s)}
                          </div>
                        );
                      })}
                      <div
                        title={
                          paidOnline
                            ? 'Pagamento online confirmado pelo gateway (ex.: Mercado Pago). O status «Faturado» será marcado pela loja após emissão da nota fiscal.'
                            : 'Acende quando o pagamento online (ex.: Mercado Pago) for confirmado pelo gateway.'
                        }
                        className={`rounded-lg border px-2 py-1 text-[10px] font-bold uppercase tracking-wide ${
                          paidOnline
                            ? 'border-emerald-500/60 bg-emerald-600 text-white shadow-sm dark:border-emerald-400/60 dark:bg-emerald-500'
                            : 'border-zinc-200 bg-white text-zinc-400 opacity-60 dark:border-zinc-700 dark:bg-zinc-900/80 dark:text-zinc-500'
                        }`}
                      >
                        $ Online
                      </div>
                      <OrderErpObsInfoButton erpObs={o.erp_obs} size="sm" />
                    </div>
                  ) : (
                    <p className="mt-2 text-xs font-medium text-red-700 dark:text-red-300">
                      {cancellationReasonText}
                    </p>
                  )}
                  <div className="mt-3 border-t border-zinc-100 pt-3 text-xs text-zinc-600 dark:border-zinc-800 dark:text-zinc-400">
                    <p>
                      <span className="font-semibold text-zinc-800 dark:text-zinc-200">Entrega:</span>{' '}
                      {[o.entrega_logradouro, o.entrega_numero, o.entrega_complemento].filter(Boolean).join(', ') || '—'}
                    </p>
                    {o.payment_method_code ? (
                      <p className="mt-1">
                        <span className="font-semibold">Pagamento:</span> {o.payment_method_code}
                      </p>
                    ) : null}
                    {o.items_preview?.length ? (
                      <ul className="mt-2 list-inside list-disc">
                        {o.items_preview.map((it, i) => (
                          <li key={i}>
                            {it.qty}× {it.nome}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                </article>
              );
            })}
            {!orders.length ? (
              <p className="text-sm text-zinc-500">Sem pedidos nesta conta.</p>
            ) : !filteredOrders.length ? (
              <p className="text-sm text-zinc-500 dark:text-zinc-400">
                Nenhum pedido com este estado. Escolha outro filtro ou «Todos».
              </p>
            ) : null}
          </div>
        ) : null}

        {tab === 'atividade' && radarEnabled ? (
          <CustomerRadarHistory slug={slug} token={token} />
        ) : null}
      </main>
    </div>
  );
}
